export const MUTE_KEY = "afae-muted";

const LEVELS = { silence: 0, gallery: 0.9, woods: 1, love: 1, reveal: 0.8, bench: 0.75, pond: 0.9, city: 0.5, whispers: 0.55, hum: 0.5, angan: 0.45, rain: 0.55, rainwalk: 0.55, duskwalk: 0.5, summer: 0.45, winter: 0.45, garden: 0.45, tanpura: 0.5, night: 0.5, nightCalm: 0.45, ghat: 0.5, clutter: 0.45, cosmos: 0.45, feather: 0.4, featherSwell: 0.6 };
const CHIME_NOTES = [77, 79, 81, 84, 86, 89, 91, 93];

class AmbientEngine {
  ctx = null;
  master = null;
  buses = {};
  scene = null;
  muted = false;
  warmth = 0;
  beatTimer = null;

  safe(fn) {
    try {
      if (this.ctx) fn();
    } catch {
      /* audio is optional */
    }
  }

  start(muted) {
    this.muted = muted;
    try {
      if (!this.ctx) {
        const AC = window.AudioContext || window.webkitAudioContext;
        if (!AC) return;
        this.ctx = new AC();
        this.master = this.ctx.createGain();
        this.master.gain.value = muted ? 0 : 0.9;
        const comp = this.ctx.createDynamicsCompressor();
        this.master.connect(comp);
        comp.connect(this.ctx.destination);
        this.white = this.noiseBuffer(false);
        this.brown = this.noiseBuffer(true);
        Object.keys(LEVELS).forEach((n) => this.bus(n));
      }
      this.ctx.resume?.().catch(() => {});
    } catch {
      this.ctx = null;
    }
  }

  noiseBuffer(brown) {
    const len = this.ctx.sampleRate * 4;
    const buf = this.ctx.createBuffer(1, len, this.ctx.sampleRate);
    const d = buf.getChannelData(0);
    let last = 0;
    for (let i = 0; i < len; i++) {
      const w = Math.random() * 2 - 1;
      if (brown) {
        last = (last + 0.02 * w) / 1.02;
        d[i] = last * 3.5;
      } else d[i] = w;
    }
    return buf;
  }

  loop(buffer, out, filterType, freq, gain, q = 0.7) {
    const s = this.ctx.createBufferSource();
    s.buffer = buffer;
    s.loop = true;
    const f = this.ctx.createBiquadFilter();
    f.type = filterType;
    f.frequency.value = freq;
    f.Q.value = q;
    const g = this.ctx.createGain();
    g.gain.value = gain;
    s.connect(f).connect(g).connect(out);
    s.start(0, Math.random() * 3);
    return { f, g };
  }

  lfo(freq, depth, param) {
    const o = this.ctx.createOscillator();
    o.frequency.value = freq;
    const g = this.ctx.createGain();
    g.gain.value = depth;
    o.connect(g).connect(param);
    o.start();
  }

  pad(freqs, out, type, cutoff, gain) {
    const f = this.ctx.createBiquadFilter();
    f.type = "lowpass";
    f.frequency.value = cutoff;
    const g = this.ctx.createGain();
    g.gain.value = gain;
    f.connect(g).connect(out);
    freqs.forEach((fr) =>
      [-4, 4].forEach((det) => {
        const o = this.ctx.createOscillator();
        o.type = type;
        o.frequency.value = fr;
        o.detune.value = det;
        o.connect(f);
        o.start();
      }),
    );
    return { f, g };
  }

  build(name, out) {
    if (name === "gallery") {
      const room = this.loop(this.brown, out, "lowpass", 260, 0.22);
      this.lfo(0.05, 60, room.f.frequency);
    } else if (name === "woods") {
      const wind = this.loop(this.white, out, "bandpass", 520, 0.12, 0.5);
      this.lfo(0.09, 0.09, wind.g.gain);
      this.lfo(0.06, 220, wind.f.frequency);
      const hiss = this.loop(this.white, out, "highpass", 3200, 0.018);
      this.lfo(0.23, 0.014, hiss.g.gain);
      this.loop(this.brown, out, "lowpass", 160, 0.22);
    } else if (name === "love") {
      this.loop(this.brown, out, "lowpass", 300, 0.08);
    } else if (name === "bench") {
      const room = this.loop(this.brown, out, "lowpass", 210, 0.2);
      this.lfo(0.03, 40, room.f.frequency);
    } else if (name === "pond") {
      const lap = this.loop(this.brown, out, "lowpass", 420, 0.22);
      this.lfo(0.13, 0.08, lap.g.gain);
      const trickle = this.loop(this.white, out, "bandpass", 1700, 0.022, 2.5);
      this.lfo(0.31, 600, trickle.f.frequency);
      this.lfo(0.17, 0.014, trickle.g.gain);
    } else if (name === "reveal") {
      this.loop(this.brown, out, "lowpass", 280, 0.1);
    } else if (name === "city") {
      const hum = this.loop(this.brown, out, "lowpass", 130, 0.26);
      this.lfo(0.04, 25, hum.f.frequency);
      const tires = this.loop(this.white, out, "bandpass", 850, 0.016, 0.8);
      this.lfo(0.07, 0.012, tires.g.gain);
    } else if (name === "rain" || name === "rainwalk") {
      // summer rain on a road: a soft hiss with patter, footsteps while walking
      this.loop(this.white, out, "lowpass", 2400, 0.06, 0.4);
      const patter = this.loop(this.white, out, "bandpass", 4200, 0.02, 1.2);
      this.lfo(0.3, 0.01, patter.g.gain);
    } else if (name === "duskwalk") {
      // an evening under tall trees: still air, leaves stirring, soft steps on earth
      this.loop(this.brown, out, "lowpass", 300, 0.12);
      const leaves = this.loop(this.white, out, "bandpass", 2600, 0.012, 1.4);
      this.lfo(0.11, 0.008, leaves.g.gain);
    } else if (name === "summer") {
      // a summer afternoon: a high insect hum and a breeze through the leaves
      const hum = this.loop(this.white, out, "bandpass", 4800, 0.01, 9);
      this.lfo(0.6, 0.006, hum.g.gain);
      const breeze = this.loop(this.white, out, "lowpass", 900, 0.05, 0.6);
      this.lfo(0.08, 0.035, breeze.g.gain);
    } else if (name === "night" || name === "nightCalm") {
      // a night forest at the cliff's edge: wind, then crickets and a breeze as the fear falls away
      const calm = name === "nightCalm";
      const wind = this.loop(this.brown, out, "lowpass", calm ? 320 : 520, calm ? 0.08 : 0.2);
      this.lfo(0.07, calm ? 0.03 : 0.1, wind.g.gain);
      const crick = this.loop(this.white, out, "bandpass", 4600, calm ? 0.02 : 0.008, 14);
      this.lfo(5.5, calm ? 0.018 : 0.007, crick.g.gain);
    } else if (name === "ghat") {
      // a summer night by the river: a warm breeze and the Ganga lapping at the steps
      const breeze = this.loop(this.white, out, "lowpass", 900, 0.035, 0.6);
      this.lfo(0.08, 0.025, breeze.g.gain);
      const lap = this.loop(this.brown, out, "lowpass", 380, 0.14);
      this.lfo(0.15, 0.07, lap.g.gain);
    } else if (name === "clutter") {
      this.loop(this.brown, out, "lowpass", 170, 0.2);
    } else if (name === "cosmos" || name === "feather" || name === "featherSwell") {
      this.pad(name === "cosmos" ? [130.8, 196, 261.6, 392] : [174.6, 261.6, 349.2], out, "sine", 900, name === "featherSwell" ? 0.05 : 0.03);
    } else if (name === "tanpura") {
      // a tanpura drone: four strings plucked in turn, ringing into one another
      this.loop(this.brown, out, "lowpass", 200, 0.04);
      this.scheduleTanpura(out);
    } else if (name === "garden") {
      // a bright garden morning: a breeze in the leaves under small footsteps
      const breeze = this.loop(this.white, out, "lowpass", 1100, 0.04, 0.6);
      this.lfo(0.09, 0.03, breeze.g.gain);
    } else if (name === "winter") {
      // a still snowy night: a low hush of wind in the evergreens
      const wind = this.loop(this.brown, out, "lowpass", 380, 0.16);
      this.lfo(0.05, 0.08, wind.g.gain);
      this.loop(this.white, out, "bandpass", 1400, 0.006, 0.5);
    } else if (name === "angan") {
      // a home at 4pm: warm indistinct murmurs of family, a faint courtyard air
      [[420, 3.1, 0.05], [700, 4.3, 0.035], [1100, 5.2, 0.02]].forEach(([f, rate, g]) => {
        const v = this.loop(this.brown, out, "bandpass", f, g * 3, 2.2);
        this.lfo(rate, g * 2.4, v.g.gain);
        this.lfo(0.21, f * 0.25, v.f.frequency);
      });
      this.loop(this.white, out, "bandpass", 5200, 0.006, 0.6);
    } else if (name === "hum") {
      // a soft humming choir: vowel-filtered voices on a calm open chord, slow vibrato
      [196, 246.9, 293.7, 392].forEach((fr, i) => {
        const v = this.pad([fr], out, "sawtooth", 900, 0.018 - i * 0.002);
        const form = this.ctx.createBiquadFilter();
        form.type = "peaking";
        form.frequency.value = 420;
        form.gain.value = 9;
        v.f.disconnect();
        v.f.connect(form).connect(v.g);
        this.lfo(0.08 + i * 0.03, 0.006, v.g.gain);
      });
    } else if (name === "whispers") {
      // layered breathy voices: narrow noise bands fluttering at speech-like rates
      [[2600, 4.1, 0.05], [3400, 5.3, 0.04], [1900, 3.2, 0.035]].forEach(([f, rate, g]) => {
        const v = this.loop(this.white, out, "bandpass", f, g, 3.5);
        this.lfo(rate, g * 0.9, v.g.gain);
        this.lfo(0.13 + rate * 0.02, 400, v.f.frequency);
      });
    } else if (name === "win_lotus") {
      const lap = this.loop(this.brown, out, "lowpass", 380, 0.16);
      this.lfo(0.11, 0.06, lap.g.gain);
      const trickle = this.loop(this.white, out, "bandpass", 1800, 0.018, 2.5);
      this.lfo(0.29, 600, trickle.f.frequency);
    } else if (name === "win_love") {
      const night = this.loop(this.white, out, "bandpass", 4600, 0.012, 9);
      this.lfo(7.5, 0.01, night.g.gain);
      this.loop(this.brown, out, "lowpass", 240, 0.08);
    } else if (name === "win_woods") {
      const wind = this.loop(this.white, out, "bandpass", 560, 0.08, 0.5);
      this.lfo(0.08, 0.06, wind.g.gain);
      this.lfo(0.05, 200, wind.f.frequency);
    }
  }

  // Living Windows: each window's world rises softly under the music as the visitor comes near.
  windowBeds(levels) {
    this.safe(() => {
      const now = this.ctx.currentTime;
      ["lotus", "love", "woods"].forEach((id) => {
        const k = levels[id] || 0;
        if (!k && !this.buses[`win_${id}`]) return;
        this.bus(`win_${id}`).gain.setTargetAtTime(k * 0.55, now, 0.6);
      });
      this.windows = { ...levels };
    });
  }

  plop(k = 1) {
    this.safe(() => {
      const t = this.ctx.currentTime;
      const o = this.ctx.createOscillator();
      const g = this.ctx.createGain();
      o.type = "sine";
      o.frequency.setValueAtTime(520 + Math.random() * 260, t);
      o.frequency.exponentialRampToValueAtTime(150, t + 0.22);
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(0.09 * k, t + 0.01);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 0.35);
      o.connect(g).connect(this.master);
      o.start(t);
      o.stop(t + 0.4);
    });
  }

  bus(name) {
    if (!this.buses[name]) {
      const g = this.ctx.createGain();
      g.gain.value = 0;
      g.connect(this.master);
      this.build(name, g);
      this.buses[name] = g;
    }
    return this.buses[name];
  }

  setScene(name) {
    this.safe(() => {
      this.bus(name);
      const now = this.ctx.currentTime;
      Object.entries(this.buses).forEach(([n, g]) => {
        if (n.startsWith("win_")) return;
        g.gain.cancelScheduledValues(now);
        g.gain.setTargetAtTime(n === name ? LEVELS[n] : 0, now, 0.9);
      });
      this.scene = name;
      if (name === "love") {
        this.warmth = 0;
        this.scheduleBeat();
      } else clearTimeout(this.beatTimer);
      if (name === "city") this.scheduleDrip();
      else clearTimeout(this.dripTimer);
      if (name === "angan") this.scheduleMemory();
      else clearTimeout(this.memTimer);
      if (name === "rainwalk" || name === "duskwalk" || name === "garden") this.scheduleStep();
      else clearTimeout(this.stepTimer);
    });
  }

  // Soft footsteps on a wet road.
  scheduleStep() {
    clearTimeout(this.stepTimer);
    this.stepTimer = setTimeout(() => {
      if (!["rainwalk", "duskwalk", "garden"].includes(this.scene)) return;
      this.safe(() => {
        const t = this.ctx.currentTime + 0.01;
        const s = this.ctx.createBufferSource();
        s.buffer = this.white;
        const f = this.ctx.createBiquadFilter();
        f.type = "bandpass";
        f.frequency.value = this.scene === "duskwalk" ? 300 : this.scene === "garden" ? 700 : 520;
        f.Q.value = 1.4;
        const g = this.ctx.createGain();
        g.gain.setValueAtTime(0.0001, t);
        g.gain.exponentialRampToValueAtTime(0.09, t + 0.015);
        g.gain.exponentialRampToValueAtTime(0.0001, t + 0.16);
        s.connect(f).connect(g).connect(this.master);
        s.start(t, Math.random() * 2);
        s.stop(t + 0.2);
      });
      this.scheduleStep();
    }, 560 + Math.random() * 60);
  }

  // Now and then a memory surfaces on its own in the angan.
  scheduleMemory() {
    clearTimeout(this.memTimer);
    this.memTimer = setTimeout(() => {
      if (this.scene !== "angan") return;
      this.memory([3, 4, 5, 6, 0, 2][Math.floor(Math.random() * 6)], 0.4);
      this.scheduleMemory();
    }, 2600 + Math.random() * 3200);
  }

  // A little echo, as if the sound came from another room of the house.
  echoBus() {
    if (this._echo) return this._echo;
    const inp = this.ctx.createGain();
    const d = this.ctx.createDelay(1);
    d.delayTime.value = 0.27;
    const fb = this.ctx.createGain();
    fb.gain.value = 0.38;
    const lp = this.ctx.createBiquadFilter();
    lp.type = "lowpass";
    lp.frequency.value = 2400;
    inp.connect(this.master);
    inp.connect(d);
    d.connect(lp).connect(fb).connect(d);
    lp.connect(this.master);
    return (this._echo = inp);
  }

  // Memory sounds, all synthesised, with a soft echo: water, bangles, a bird, laughter, utensils, a broom, a grinding stone.
  memory(i, k = 1) {
    this.safe(() => {
      const t = this.ctx.currentTime + 0.02;
      const out = this.echoBus();
      const env = (g, a, peak, d) => {
        g.gain.setValueAtTime(0.0001, a);
        g.gain.exponentialRampToValueAtTime(peak, a + 0.02);
        g.gain.exponentialRampToValueAtTime(0.0001, a + d);
      };
      const kind = ((i % 7) + 7) % 7;
      const noise = (a, d, f, q, peak) => {
        const s = this.ctx.createBufferSource();
        s.buffer = this.white;
        const fl = this.ctx.createBiquadFilter();
        fl.type = "bandpass";
        fl.frequency.value = f;
        fl.Q.value = q;
        const g = this.ctx.createGain();
        g.gain.setValueAtTime(0.0001, a);
        g.gain.exponentialRampToValueAtTime(peak, a + d * 0.35);
        g.gain.exponentialRampToValueAtTime(0.0001, a + d);
        s.connect(fl).connect(g).connect(out);
        s.start(a, Math.random() * 2);
        s.stop(a + d + 0.05);
        return fl;
      };
      if (kind === 4) {
        // steel utensils set down: two bright clinks with inharmonic ring
        [0, 0.32].forEach((dt) =>
          [1180, 2710, 4230].forEach((fr, j) => {
            const o = this.ctx.createOscillator();
            o.frequency.value = fr * (1 + dt * 0.04);
            const g = this.ctx.createGain();
            env(g, t + dt, (0.03 - j * 0.007) * k, 0.7);
            o.connect(g).connect(out);
            o.start(t + dt);
            o.stop(t + dt + 0.75);
          }),
        );
        return;
      }
      if (kind === 5) {
        [0, 0.42, 0.84].forEach((dt) => noise(t + dt, 0.32, 3200, 0.9, 0.08 * k));
        return;
      }
      if (kind === 6) {
        // a grinding stone turning: a low rough rumble, swelling twice
        [0, 0.9].forEach((dt) => {
          const f = noise(t + dt, 0.8, 180, 1.2, 0.2 * k);
          f.frequency.setValueAtTime(150, t + dt);
          f.frequency.linearRampToValueAtTime(240, t + dt + 0.4);
          f.frequency.linearRampToValueAtTime(150, t + dt + 0.8);
        });
        return;
      }
      if (kind === 0) {
        const s = this.ctx.createBufferSource();
        s.buffer = this.white;
        const f = this.ctx.createBiquadFilter();
        f.type = "bandpass";
        f.Q.value = 6;
        f.frequency.setValueAtTime(380, t);
        f.frequency.exponentialRampToValueAtTime(1500, t + 1.6);
        const g = this.ctx.createGain();
        g.gain.setValueAtTime(0.0001, t);
        g.gain.exponentialRampToValueAtTime(0.18 * k, t + 0.15);
        g.gain.setTargetAtTime(0.0001, t + 1.5, 0.15);
        s.connect(f).connect(g).connect(out);
        s.start(t, Math.random());
        s.stop(t + 2.2);
      } else if (kind === 1) {
        [0, 0.09, 0.15, 0.27].forEach((dt, j) =>
          [2630, 3710, 5120].forEach((fr) => {
            const o = this.ctx.createOscillator();
            o.frequency.value = fr * (1 + j * 0.013);
            const g = this.ctx.createGain();
            env(g, t + dt, 0.02 * k, 0.5);
            o.connect(g).connect(out);
            o.start(t + dt);
            o.stop(t + dt + 0.55);
          }),
        );
      } else if (kind === 2) {
        [0, 0.22, 0.38].forEach((dt) => {
          const o = this.ctx.createOscillator();
          o.frequency.setValueAtTime(2900, t + dt);
          o.frequency.exponentialRampToValueAtTime(4100, t + dt + 0.09);
          o.frequency.exponentialRampToValueAtTime(3200, t + dt + 0.14);
          const g = this.ctx.createGain();
          env(g, t + dt, 0.025 * k, 0.16);
          o.connect(g).connect(out);
          o.start(t + dt);
          o.stop(t + dt + 0.2);
        });
      } else {
        [0, 0.17, 0.33, 0.5, 0.66].forEach((dt, j) => {
          const s = this.ctx.createBufferSource();
          s.buffer = this.white;
          const f = this.ctx.createBiquadFilter();
          f.type = "bandpass";
          f.Q.value = 5;
          f.frequency.value = 900 - j * 60;
          const g = this.ctx.createGain();
          env(g, t + dt, 0.12 * k * (1 - j * 0.15), 0.13);
          s.connect(f).connect(g).connect(out);
          s.start(t + dt, Math.random() * 2);
          s.stop(t + dt + 0.16);
        });
      }
    });
  }

  // City after the rain: an occasional drip from a ledge into a puddle, quiet under the music.
  scheduleDrip() {
    clearTimeout(this.dripTimer);
    this.dripTimer = setTimeout(() => {
      if (this.scene !== "city") return;
      this.plop(0.28);
      this.scheduleDrip();
    }, 1300 + Math.random() * 2200);
  }

  scheduleBeat() {
    clearTimeout(this.beatTimer);
    this.beatTimer = setTimeout(() => {
      if (this.scene !== "love") return;
      this.safe(() => {
        const t = this.ctx.currentTime + 0.02;
        this.thump(t, 1);
        this.thump(t + 0.27, 0.55);
      });
      this.scheduleBeat();
    }, 1050 - this.warmth * 300);
  }

  // A palette knife dragged across a dry, textured surface.
  scrape() {
    this.safe(() => {
      const t = this.ctx.currentTime + 0.02;
      const s = this.ctx.createBufferSource();
      s.buffer = this.white;
      const f = this.ctx.createBiquadFilter();
      f.type = "bandpass";
      f.Q.value = 3;
      f.frequency.setValueAtTime(2200, t);
      f.frequency.linearRampToValueAtTime(3400, t + 1.0);
      const g = this.ctx.createGain();
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(0.09, t + 0.08);
      g.gain.setValueAtTime(0.09, t + 0.8);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 1.1);
      const am = this.ctx.createGain();
      const lfo = this.ctx.createOscillator();
      lfo.frequency.value = 23;
      const depth = this.ctx.createGain();
      depth.gain.value = 0.5;
      lfo.connect(depth).connect(am.gain);
      s.connect(f).connect(am).connect(g).connect(this.master);
      s.start(t, Math.random());
      lfo.start(t);
      s.stop(t + 1.2);
      lfo.stop(t + 1.2);
    });
  }

  // A soft single pulse under the music (the Storm's whirlpool).
  heartbeat(k = 0.5) {
    this.safe(() => {
      if (k <= 0.02) return;
      const t = this.ctx.currentTime + 0.02;
      const o = this.ctx.createOscillator();
      o.frequency.setValueAtTime(70, t);
      o.frequency.exponentialRampToValueAtTime(38, t + 0.18);
      const g = this.ctx.createGain();
      g.gain.setValueAtTime(0.0001, t);
      g.gain.linearRampToValueAtTime(0.22 * k, t + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 0.3);
      o.connect(g).connect(this.master);
      o.start(t);
      o.stop(t + 0.35);
    });
  }

  thump(t, amp) {
    const o = this.ctx.createOscillator();
    o.frequency.setValueAtTime(96, t);
    o.frequency.exponentialRampToValueAtTime(40, t + 0.15);
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.linearRampToValueAtTime(0.5 * amp, t + 0.014);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.24);
    o.connect(g).connect(this.bus("love"));
    o.start(t);
    o.stop(t + 0.3);
  }

  burst(freq, q, peak, dur, busName) {
    const s = this.ctx.createBufferSource();
    s.buffer = this.white;
    const f = this.ctx.createBiquadFilter();
    f.type = "bandpass";
    f.frequency.value = freq;
    f.Q.value = q;
    const g = this.ctx.createGain();
    const t = this.ctx.currentTime;
    g.gain.setValueAtTime(0.0001, t);
    g.gain.linearRampToValueAtTime(peak, t + dur * 0.25);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    s.connect(f).connect(g).connect(this.bus(busName));
    s.start(t, Math.random() * 3, dur + 0.1);
  }

  rustle(intensity = 1, busName = "woods") {
    this.safe(() => this.burst(2400 + Math.random() * 2200, 0.9, 0.09 * intensity, 0.35, busName));
  }

  // A recorded one-shot (a temple bell), outside the music manager so it never doubles a track.
  // A looping bed (a deep OM) through a long, temple-like convolution reverb; the journey sets its level as it goes.
  bed(url, loopEnd = 32) {
    const c = { g: null, s: null, off: false };
    const ctx = this.ctx;
    if (!ctx) return { set() {}, stop() {} };
    fetch(url)
      .then((r) => r.arrayBuffer())
      .then((b) => ctx.decodeAudioData(b))
      .then((buf) => {
        if (c.off) return;
        const rev = ctx.createConvolver();
        const len = Math.round(ctx.sampleRate * 5.5);
        const ir = ctx.createBuffer(2, len, ctx.sampleRate);
        for (let ch = 0; ch < 2; ch++) {
          const d = ir.getChannelData(ch);
          for (let i = 0; i < len; i++) d[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / len, 3.2);
        }
        rev.buffer = ir;
        c.g = ctx.createGain();
        c.g.gain.value = 0.0001;
        const dry = ctx.createGain();
        dry.gain.value = 0.55;
        c.s = ctx.createBufferSource();
        c.s.buffer = buf;
        c.s.loop = true;
        c.s.loopEnd = Math.min(loopEnd, buf.duration);
        c.s.connect(c.g);
        c.g.connect(dry).connect(this.master);
        c.g.connect(rev).connect(this.master);
        c.s.start();
      })
      .catch(() => {});
    return {
      set: (v) => c.g && !this.muted && c.g.gain.setTargetAtTime(Math.max(0.0001, v), ctx.currentTime, 0.6),
      stop: () => {
        c.off = true;
        if (!c.g) return;
        c.g.gain.setTargetAtTime(0.0001, ctx.currentTime, 0.8);
        const s = c.s;
        setTimeout(() => s.stop(), 4000);
      },
    };
  }

  // cues are fetched and decoded once, then started sample-accurately on the audio clock
  cue(url) {
    this.cues = this.cues || {};
    if (!this.cues[url] && this.ctx) this.cues[url] = fetch(url).then((r) => r.arrayBuffer()).then((b) => this.ctx.decodeAudioData(b)).catch(() => null);
    return this.cues[url];
  }

  preloadCues(urls) {
    urls.forEach((u) => this.cue(u));
  }

  // a looping recorded bed (e.g. footsteps) whose level and pace follow the story each frame
  stepsBed(url) {
    if (!this.ctx) return null;
    const g = this.ctx.createGain();
    g.gain.value = 0;
    g.connect(this.master);
    const h = { src: null, g, set: (v, rate) => {
      if (!h.src) return;
      g.gain.setTargetAtTime(this.muted ? 0 : v, this.ctx.currentTime, 0.06);
      h.src.playbackRate.setTargetAtTime(rate, this.ctx.currentTime, 0.1);
    }, stop: () => {
      h.dead = true;
      g.gain.setTargetAtTime(0, this.ctx.currentTime, 0.08);
      setTimeout(() => this.safe(() => (h.src?.stop(), g.disconnect())), 600);
    } };
    this.cue(url)?.then((buf) => {
      if (!buf || h.dead) return;
      h.src = this.ctx.createBufferSource();
      h.src.buffer = buf;
      h.src.loop = true;
      h.src.connect(g);
      h.src.start();
    });
    return h;
  }

  sample(url, vol = 0.6) {
    if (this.muted || !this.ctx) return;
    this.cue(url)?.then((buf) => {
      if (!buf || this.muted) return;
      const s = this.ctx.createBufferSource();
      const g = this.ctx.createGain();
      const t = this.ctx.currentTime + 0.01;
      g.gain.setValueAtTime(0, t);
      g.gain.linearRampToValueAtTime(vol, t + 0.02);
      s.buffer = buf;
      s.connect(g).connect(this.master);
      s.start(t);
    });
  }

  scheduleTanpura(out) {
    clearTimeout(this.tanTimer);
    const notes = [196, 261.63, 261.63, 130.81];
    let i = 0;
    const pluck = () => {
      if (this.scene !== "tanpura") return;
      this.safe(() => {
        const t = this.ctx.currentTime;
        const f = notes[i++ % 4];
        [1, 2.003, 3.007].forEach((m, j) => {
          const o = this.ctx.createOscillator();
          o.type = j ? "sine" : "sawtooth";
          o.frequency.value = f * m;
          const lp = this.ctx.createBiquadFilter();
          lp.type = "lowpass";
          lp.frequency.value = 1600;
          const g = this.ctx.createGain();
          g.gain.setValueAtTime(0.0001, t);
          g.gain.exponentialRampToValueAtTime(0.028 / (j + 1), t + 0.04);
          g.gain.exponentialRampToValueAtTime(0.0001, t + 4.6);
          o.connect(lp).connect(g).connect(out);
          o.start(t);
          o.stop(t + 4.7);
        });
      });
      this.tanTimer = setTimeout(pluck, 1200);
    };
    pluck();
  }

  // A far-off owl: two soft hoots.
  owl() {
    this.safe(() => {
      [0, 0.55].forEach((d, k) => {
        const t = this.ctx.currentTime + 0.05 + d;
        const o = this.ctx.createOscillator();
        o.type = "sine";
        o.frequency.setValueAtTime(k ? 360 : 400, t);
        o.frequency.exponentialRampToValueAtTime(k ? 330 : 370, t + 0.35);
        const g = this.ctx.createGain();
        g.gain.setValueAtTime(0.0001, t);
        g.gain.exponentialRampToValueAtTime(0.05, t + 0.06);
        g.gain.exponentialRampToValueAtTime(0.0001, t + 0.42);
        o.connect(g).connect(this.master);
        o.start(t);
        o.stop(t + 0.45);
      });
    });
  }

  bellSoft() {
    this.sample(`${process.env.PUBLIC_URL || ""}/sounds/shiva/bell.mp3`, 0.28);
  }

  // A shehnai's festive phrase: a nasal reed gliding down through Bhairav, with vibrato (synthesised here, not a recording).
  shehnai() {
    this.safe(() => {
      const ctx = this.ctx;
      const t0 = ctx.currentTime + 0.05;
      const notes = [392, 415.3, 392, 349.2, 329.6, 349.2, 329.6, 277.2, 261.6];
      const o = ctx.createOscillator();
      o.type = "sawtooth";
      o.frequency.setValueAtTime(notes[0] * 2, t0);
      notes.forEach((f, i) => o.frequency.setTargetAtTime(f * 2, t0 + i * 0.42, 0.04));
      const vib = ctx.createOscillator();
      const vg = ctx.createGain();
      vib.frequency.value = 5.5;
      vg.gain.value = 5;
      vib.connect(vg).connect(o.frequency);
      const bp = ctx.createBiquadFilter();
      bp.type = "bandpass";
      bp.frequency.value = 1300;
      bp.Q.value = 1.4;
      const lp = ctx.createBiquadFilter();
      lp.type = "lowpass";
      lp.frequency.value = 3200;
      const g = ctx.createGain();
      const end = t0 + notes.length * 0.42 + 0.4;
      g.gain.setValueAtTime(0.0001, t0);
      g.gain.exponentialRampToValueAtTime(0.045, t0 + 0.12);
      g.gain.setValueAtTime(0.045, end - 0.5);
      g.gain.exponentialRampToValueAtTime(0.0001, end);
      o.connect(bp).connect(lp).connect(g).connect(this.master);
      g.connect(this.echoBus());
      o.start(t0);
      vib.start(t0);
      o.stop(end + 0.05);
      vib.stop(end + 0.05);
    });
  }

  // Distant laughter from somewhere in the celebration: a few breathy "ha"s.
  laugh() {
    this.safe(() => {
      const t0 = this.ctx.currentTime + 0.05;
      const f0 = 700 + Math.random() * 250;
      for (let k = 0; k < 5; k++) {
        const t = t0 + k * 0.17;
        const s = this.ctx.createBufferSource();
        s.buffer = this.white;
        const f = this.ctx.createBiquadFilter();
        f.type = "bandpass";
        f.frequency.value = f0 * (1 - k * 0.04);
        f.Q.value = 5;
        const g = this.ctx.createGain();
        g.gain.setValueAtTime(0.0001, t);
        g.gain.exponentialRampToValueAtTime(0.09 * (1 - k * 0.15), t + 0.03);
        g.gain.exponentialRampToValueAtTime(0.0001, t + 0.14);
        s.connect(f).connect(g).connect(this.master);
        s.start(t, Math.random());
        s.stop(t + 0.16);
      }
    });
  }

  // A crowded room's clock: tick-tock for about eight seconds, stopping once the room changes.
  ticks() {
    let n = 0;
    clearInterval(this.tickTimer);
    this.tickTimer = setInterval(() => {
      if (++n > 16 || this.scene !== "clutter") return clearInterval(this.tickTimer);
      this.safe(() => this.burst(n % 2 ? 2600 : 2100, 6, 0.05, 0.05, "clutter"));
    }, 500);
  }

  // A conch at the water's edge, and water poured from a lota for arghya (recordings credited in the Credits panel).
  dhak() {
    this.sample?.("/sounds/home/dhak.mp3", 0.7);
  }

  conch() {
    this.sample?.("/sounds/home/conch.mp3", 0.4);
  }

  pour() {
    this.safe(() => this.burst(2400, 0.6, 0.05, 2.6, this.scene));
  }

  // The clink of cooking in a lit kitchen, far off.
  clink() {
    this.memory(4, 0.45);
  }

  // A fish leaping out of the water: a splash, then a soft whoosh into the sky.
  fish() {
    this.plop(0.6);
    this.safe(() => this.burst(1800, 0.7, 0.06, 1.2, this.scene));
  }

  // A distant damru: a quick rattle of double strokes.
  damru() {
    this.safe(() => {
      const t0 = this.ctx.currentTime + 0.02;
      for (let k = 0; k < 14; k++) {
        const t = t0 + k * 0.085;
        const o = this.ctx.createOscillator();
        o.type = "sine";
        o.frequency.setValueAtTime(k % 2 ? 190 : 240, t);
        o.frequency.exponentialRampToValueAtTime(110, t + 0.07);
        const g = this.ctx.createGain();
        g.gain.setValueAtTime(0.0001, t);
        g.gain.exponentialRampToValueAtTime(0.07 * (1 - k / 18), t + 0.005);
        g.gain.exponentialRampToValueAtTime(0.0001, t + 0.08);
        o.connect(g).connect(this.master);
        o.start(t);
        o.stop(t + 0.09);
      }
    });
  }

  birdsong() {
    this.memory(2, 0.8);
  }

  // Sounds of life on the planet: birdsong, rain, a whale's call, wind in leaves.
  life(i) {
    if (i === 0) return this.memory(2, 0.9);
    if (i === 1) return this.safe(() => this.burst(4200, 0.5, 0.06, 2.2, this.scene));
    if (i === 3) return this.safe(() => this.burst(700, 0.5, 0.2, 2.4, this.scene));
    this.safe(() => {
      const t = this.ctx.currentTime;
      const o = this.ctx.createOscillator();
      const v = this.ctx.createOscillator();
      const vg = this.ctx.createGain();
      const g = this.ctx.createGain();
      const f = this.ctx.createBiquadFilter();
      f.type = "lowpass";
      f.frequency.value = 900;
      o.type = "sine";
      o.frequency.setValueAtTime(170, t);
      o.frequency.exponentialRampToValueAtTime(260, t + 1.1);
      o.frequency.exponentialRampToValueAtTime(120, t + 2.8);
      v.frequency.value = 5;
      vg.gain.value = 6;
      v.connect(vg).connect(o.frequency);
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(0.12, t + 0.5);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 3);
      o.connect(f).connect(g).connect(this.master);
      o.start(t);
      v.start(t);
      o.stop(t + 3.1);
      v.stop(t + 3.1);
    });
  }

  // Snow shaken off a branch: a soft, low puff.
  puff() {
    this.safe(() => this.burst(520 + Math.random() * 160, 0.6, 0.16, 0.9, this.scene));
  }

  bell() {
    this.safe(() => {
      const t = this.ctx.currentTime;
      const out = this.ctx.createGain();
      out.gain.value = 0.18;
      out.connect(this.master);
      [[1, 1], [2.76, 0.5], [5.4, 0.25], [8.93, 0.12]].forEach(([k, a]) => {
        const o = this.ctx.createOscillator();
        const g = this.ctx.createGain();
        o.frequency.value = 392 * k;
        g.gain.setValueAtTime(0, t);
        g.gain.linearRampToValueAtTime(a, t + 0.01);
        g.gain.exponentialRampToValueAtTime(0.0001, t + 5 / Math.sqrt(k));
        o.connect(g).connect(out);
        o.start(t);
        o.stop(t + 5.2);
      });
    });
  }

  rain(on) {
    this.safe(() => {
      if (!this.rainBus) {
        this.rainBus = this.ctx.createGain();
        this.rainBus.gain.value = 0;
        this.rainBus.connect(this.master);
        this.loop(this.white, this.rainBus, "highpass", 2400, 0.045);
        const patter = this.loop(this.white, this.rainBus, "bandpass", 950, 0.03, 0.8);
        this.lfo(0.21, 0.01, patter.g.gain);
      }
      this.rainBus.gain.setTargetAtTime(on ? 1 : 0, this.ctx.currentTime, on ? 0.6 : 1.2);
    });
  }

  gust() {
    this.safe(() => this.burst(800, 0.5, 0.25, 2.2, "woods"));
  }

  pop() {
    this.safe(() => {
      const t = this.ctx.currentTime;
      this.chimes = (this.chimes || []).filter((end) => end > t);
      if (this.chimes.length >= 3) return;
      const midi = CHIME_NOTES[Math.floor(Math.random() * CHIME_NOTES.length)];
      const f = 440 * Math.pow(2, (midi - 69) / 12) * Math.pow(2, (Math.random() * 12 - 6) / 1200);
      const vel = 0.55 + Math.random() * 0.45;
      const out = this.ctx.createGain();
      out.gain.value = 0.055 * vel;
      const lp = this.ctx.createBiquadFilter();
      lp.type = "lowpass";
      lp.frequency.value = 7500;
      out.connect(lp).connect(this.master);
      const tone = (freq, peak, start, decay, detune = 0) => {
        const o = this.ctx.createOscillator();
        o.frequency.value = freq;
        o.detune.value = detune;
        const g = this.ctx.createGain();
        g.gain.setValueAtTime(0.0001, start);
        g.gain.linearRampToValueAtTime(peak, start + 0.004);
        g.gain.exponentialRampToValueAtTime(0.0001, start + decay);
        o.connect(g).connect(out);
        o.start(start);
        o.stop(start + decay + 0.05);
      };
      tone(f, 1, t, 1.4);
      tone(f * 2.756, 0.22, t, 0.4);
      tone(f * 5.404, 0.08, t, 0.16);
      [[2, 7], [2, -7], [3, 4]].forEach(([r, c], k) => tone(f * r, 0.07, t + 0.03 + k * 0.025, 1.9, c));
      this.chimes.push(t + 0.7);
      this.lastChime = { midi, vel: +vel.toFixed(2) };
    });
  }

  // A soft hush and two or three faint, echoing footsteps under the music (about -24 dB), a little different every time.
  hush() {
    if (this.muted || !this.ctx) return (this.lastHush = { skipped: this.muted ? "muted" : "no-audio", at: Date.now() });
    this.safe(() => {
      const ctx = this.ctx;
      const t = ctx.currentTime + 0.05;
      const db = -24 + (Math.random() * 2 - 1);
      const out = ctx.createGain();
      out.gain.value = 0.9 * Math.pow(10, db / 20);
      const echo = ctx.createDelay(1);
      echo.delayTime.value = 0.15 + Math.random() * 0.1;
      const fb = ctx.createGain();
      fb.gain.value = 0.3;
      const wet = ctx.createGain();
      wet.gain.value = 0.45;
      out.connect(this.master);
      out.connect(echo);
      echo.connect(fb).connect(echo);
      echo.connect(wet).connect(this.master);
      const noise = (buf, type, freq, q, start, peak, attack, len) => {
        const s = ctx.createBufferSource();
        s.buffer = buf;
        const f = ctx.createBiquadFilter();
        f.type = type;
        f.frequency.value = freq;
        f.Q.value = q;
        const g = ctx.createGain();
        g.gain.setValueAtTime(0.0001, start);
        g.gain.linearRampToValueAtTime(peak, start + attack);
        g.gain.exponentialRampToValueAtTime(0.0001, start + len);
        s.connect(f).connect(g).connect(out);
        s.start(start, Math.random() * 3, len + 0.1);
      };
      const hushLen = 1.1 + Math.random() * 0.5;
      noise(this.white, "bandpass", 2200 + Math.random() * 1200, 1.1, t, 0.9, hushLen * 0.35, hushLen);
      noise(this.white, "highpass", 5200, 0.7, t + 0.08, 0.25, hushLen * 0.3, hushLen * 0.8);
      const steps = Math.random() < 0.5 ? 2 : 3;
      let at = t + 0.5 + Math.random() * 0.2;
      for (let k = 0; k < steps; k++) {
        const amp = 1 - k * 0.18;
        noise(this.brown, "lowpass", 260 + Math.random() * 140, 0.8, at, 1.4 * amp, 0.008, 0.2);
        noise(this.white, "bandpass", 1600 + Math.random() * 600, 2, at, 0.12 * amp, 0.004, 0.05);
        at += 0.46 + Math.random() * 0.14;
      }
      setTimeout(() => [out, echo, fb, wet].forEach((n) => n.disconnect()), (at - ctx.currentTime + 2.5) * 1000);
      this.lastHush = { at: Date.now(), db: +db.toFixed(1), steps, hush: +hushLen.toFixed(2), gain: +out.gain.value.toFixed(4) };
    });
    return this.lastHush;
  }

  setWarmth(w) {
    this.warmth = w;
  }

  state() {
    return { context: this.ctx?.state || "none", muted: this.muted, masterGain: this.master ? +this.master.gain.value.toFixed(3) : null, scene: this.scene, chimesActive: this.ctx ? (this.chimes || []).filter((e) => e > this.ctx.currentTime).length : 0, lastChime: this.lastChime || null, lastHush: this.lastHush || null, windows: this.windows || {} };
  }

  setMuted(m) {
    this.muted = m;
    this.safe(() => this.master.gain.setTargetAtTime(m ? 0 : this.ducked ? 0.28 : 0.9, this.ctx.currentTime, 0.12));
  }

  // everything (music + ambience) softly lower while an overlay such as The Flipbook is open
  duck(on) {
    this.ducked = on;
    this.safe(() => this.master.gain.setTargetAtTime(this.muted ? 0 : on ? 0.28 : 0.9, this.ctx.currentTime, 0.5));
  }
}

export const audio = new AmbientEngine();
